import { z } from 'zod';

import type { QueryParamValue } from './visitorSource';

export { type QueryParamValue, type QueryParams } from './visitorSource';

const UrlOrEmptyStringSchema = z.union([z.string().url(), z.literal('')]);
const ReferrerSchema = UrlOrEmptyStringSchema.nullable().optional();

export const DeviceTypeSchema = z.enum(['mobile', 'desktop']);

const QueryParamValueSchema: z.ZodType<QueryParamValue> = z.lazy(() =>
  z.union([z.string(), z.array(QueryParamValueSchema), z.record(z.string(), QueryParamValueSchema)]),
);

export const QueryParamsSchema = z.record(z.string(), QueryParamValueSchema);
export const QueryParamsMapSchema = z.record(z.string(), z.string());

export const AnalyticsEventType = {
  PageView: 'page_view',
  OneShotTokenPageView: 'one_shot_token_page_view',
  Identify: 'identify',
  RrwebClick: 'rrweb_click',
  RrwebTime: 'rrweb_time',
  RrwebData: 'rrweb_data',
} as const;

export const PageViewPropertiesSchema = z
  .object({
    url: z.string().url(),
    title: z.string(),
    referrer: ReferrerSchema,
  })
  .strict();

export const IdentifyPropertiesSchema = z
  .object({
    email: z.string().email(),
  })
  .strict();

export type RrwebJsonValue =
  | string
  | number
  | boolean
  | null
  | RrwebJsonValue[]
  | {
      [key: string]: RrwebJsonValue;
    };

const RrwebJsonValueSchema: z.ZodType<RrwebJsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(RrwebJsonValueSchema),
    z.record(z.string(), RrwebJsonValueSchema),
  ]),
);

export const RrwebEventSchema = z
  .object({
    type: z.number().int().nonnegative(),
    data: RrwebJsonValueSchema,
    timestamp: z.number().int().nonnegative(),
    delay: z.number().optional(),
  })
  .strict();

export const RrwebRecordedClickEventSchema = z
  .object({
    type: z.literal(3),
    data: z
      .object({
        source: z.literal(2),
        type: z.literal(2),
        id: z.number().int().nonnegative(),
        x: z.number().optional(),
        y: z.number().optional(),
        pointerType: z.number().int().nonnegative().optional(),
      })
      .strict(),
    timestamp: z.number().int().nonnegative(),
    delay: z.number().optional(),
  })
  .strict();

export const RrwebDataPropertiesSchema = z
  .object({
    events: z.array(RrwebEventSchema).min(1),
  })
  .strict();

export const RrwebDataMetadataPropertiesSchema = z
  .object({
    s3_key: z.string().min(1),
    event_count: z.number().int().positive(),
    click_count: z.number().int().nonnegative(),
    engaged_time_ms: z.number().int().nonnegative(),
    first_timestamp: z.number().int().nonnegative(),
    last_timestamp: z.number().int().nonnegative(),
  })
  .strict();

const BaseClientEventSchema = z
  .object({
    event_id: z.string().uuid(),
    client_ts: z.number().int().nonnegative(),
  })
  .strict();

export const PageViewEventSchema = BaseClientEventSchema.extend({
  event_type: z.literal(AnalyticsEventType.PageView),
  has_session_replay: z.boolean().optional(),
  properties: PageViewPropertiesSchema,
});

export const OneShotTokenPageViewPropertiesSchema = PageViewPropertiesSchema.extend({
  token: z.string().min(1),
});

export const OneShotTokenPageViewEventSchema = BaseClientEventSchema.extend({
  event_type: z.literal(AnalyticsEventType.OneShotTokenPageView),
  has_session_replay: z.boolean().optional(),
  properties: OneShotTokenPageViewPropertiesSchema,
});

export const IdentifyEventSchema = BaseClientEventSchema.extend({
  event_type: z.literal(AnalyticsEventType.Identify),
  properties: IdentifyPropertiesSchema,
});

export const RrwebDataEventSchema = BaseClientEventSchema.extend({
  event_type: z.literal(AnalyticsEventType.RrwebData),
  page_view_id: z.string().uuid(),
  properties: RrwebDataPropertiesSchema,
});

export const RrwebClickEventSchema = BaseClientEventSchema.extend({
  event_type: z.literal(AnalyticsEventType.RrwebClick),
  page_view_id: z.string().uuid(),
});

export const RrwebTimePropertiesSchema = z
  .object({
    engaged_time_ms: z.number().int().nonnegative(),
    first_timestamp: z.number().int().nonnegative(),
    last_timestamp: z.number().int().nonnegative(),
  })
  .strict()
  .refine((properties) => properties.last_timestamp >= properties.first_timestamp, {
    message: 'last_timestamp must not precede first_timestamp',
    path: ['last_timestamp'],
  });

export const RrwebTimeEventSchema = BaseClientEventSchema.extend({
  event_type: z.literal(AnalyticsEventType.RrwebTime),
  page_view_id: z.string().uuid(),
  properties: RrwebTimePropertiesSchema,
});

export const AnalyticsEventSchema = z.discriminatedUnion('event_type', [
  PageViewEventSchema,
  OneShotTokenPageViewEventSchema,
  IdentifyEventSchema,
  RrwebClickEventSchema,
  RrwebDataEventSchema,
]);

export const EventEnvelopeSchema = z
  .object({
    company_id: z.string().min(1),
    asset_id: z.string().min(1),
    member_key: z.string().min(1).nullable().optional(),
    visitor_id: z.string().uuid(),
    events: z.array(AnalyticsEventSchema).min(1).max(100),
  })
  .strict();

export type AnalyticsEventInput = z.infer<typeof AnalyticsEventSchema>;
export type EventEnvelopeInput = z.infer<typeof EventEnvelopeSchema>;
export type PageViewEventInput = z.infer<typeof PageViewEventSchema>;
export type OneShotTokenPageViewEventInput = z.infer<typeof OneShotTokenPageViewEventSchema>;
export type RrwebEventInput = z.infer<typeof RrwebEventSchema>;
export type RrwebRecordedClickEventInput = z.infer<typeof RrwebRecordedClickEventSchema>;
export type RrwebClickEventInput = z.infer<typeof RrwebClickEventSchema>;
export type RrwebDataEventInput = z.infer<typeof RrwebDataEventSchema>;

export const ServerEventFieldsSchema = z
  .object({
    server_ts: z.string().datetime(),
    source_ip: z.string(),
    company_id: z.string().min(1),
    asset_id: z.string().min(1),
    member_key: z.string().min(1).nullable().optional(),
    visitor_id: z.string().uuid(),
  })
  .strict();

export const EnrichedEventTsSchema = z
  .object({
    event_ts: ServerEventFieldsSchema.shape.server_ts,
  })
  .strict();

export const RawPageViewPropertiesSchema = PageViewPropertiesSchema.extend({
  url_host: z.string(),
  url_path: z.string(),
  url_query: z.string(),
  query_params: QueryParamsSchema,
  query_params_map: QueryParamsMapSchema.optional(),
});

export const RawPageViewServerFieldsSchema = z
  .object({
    user_agent: z.string().nullable(),
    device_type: DeviceTypeSchema.nullable().optional(),
  })
  .strict();

export const RawOneShotTokenIdentitySchema = z
  .object({
    email: z.string().email(),
  })
  .strict();

export const OneShotProviderSchema = z.enum(['salesloft', 'outreach']);

export const PageViewWafMetadataSchema = z
  .object({
    is_bot: z.boolean().optional(),
    location: z.string().min(1).optional(),
    cloudfront_viewer_address: z.string().min(1).optional(),
    cloudfront_viewer_asn: z.string().min(1).optional(),
    cloudfront_viewer_city: z.string().min(1).optional(),
    cloudfront_viewer_country: z.string().min(1).optional(),
    cloudfront_viewer_country_name: z.string().min(1).optional(),
    cloudfront_viewer_country_region: z.string().min(1).optional(),
    cloudfront_viewer_country_region_name: z.string().min(1).optional(),
    cloudfront_viewer_latitude: z.string().min(1).optional(),
    cloudfront_viewer_longitude: z.string().min(1).optional(),
    cloudfront_viewer_metro_code: z.string().min(1).optional(),
    cloudfront_viewer_postal_code: z.string().min(1).optional(),
    cloudfront_viewer_time_zone: z.string().min(1).optional(),
  })
  .strict();

export const RawPageViewEventSchema = PageViewEventSchema.extend(ServerEventFieldsSchema.shape)
  .extend(RawPageViewServerFieldsSchema.shape)
  .extend(PageViewWafMetadataSchema.shape)
  .extend({
    properties: RawPageViewPropertiesSchema,
    one_shot_token: RawOneShotTokenIdentitySchema.optional(),
    one_shot_provider: OneShotProviderSchema.optional(),
  });

export const RawIdentifyEventSchema = IdentifyEventSchema.extend(ServerEventFieldsSchema.shape);

export const RawRrwebClickEventSchema = RrwebClickEventSchema.extend(ServerEventFieldsSchema.shape);

export const RawRrwebTimeEventSchema = RrwebTimeEventSchema.extend(ServerEventFieldsSchema.shape);

export const RecordingEventSchema = RrwebDataEventSchema.extend(ServerEventFieldsSchema.shape);

export const RawRrwebDataEventSchema = BaseClientEventSchema.extend(ServerEventFieldsSchema.shape).extend({
  event_type: z.literal(AnalyticsEventType.RrwebData),
  page_view_id: z.string().uuid(),
  properties: RrwebDataMetadataPropertiesSchema,
});

export const RawAnalyticsEventSchema = z.discriminatedUnion('event_type', [
  RawPageViewEventSchema,
  RawIdentifyEventSchema,
  RawRrwebClickEventSchema,
  RawRrwebTimeEventSchema,
]);

export const RawAnalyticsStorageEventSchema = z.union([RawAnalyticsEventSchema, RawRrwebDataEventSchema]);

export const SourceKafkaSchema = z
  .object({
    topic: z.string().min(1),
    partition: z.number().int().nonnegative(),
    offset: z.string().regex(/^\d+$/),
    timestamp: z.string().datetime(),
  })
  .strict();

export const EnrichedIdentitySchema = z
  .object({
    email: z.string().email(),
    email_source: z.string().min(1),
    identity_resolved_at: z.string().datetime(),
    is_known: z.literal(true),
  })
  .strict();

export const VisitorIdentitySchema = z
  .object({
    email: z.string().email(),
    email_source: z.string().min(1),
    identity_resolved_at: z.string().datetime(),
  })
  .strict();

export const PageViewStateSchema = z.enum(['identified', 'engaged', 'non-engaged', 'internal', 'bot']);

const EnrichedSourceKafkaSchema = z
  .object({
    source_kafka: z
      .object({
        topic: SourceKafkaSchema.shape.topic.nullable(),
        partition: SourceKafkaSchema.shape.partition.nullable(),
        offset: SourceKafkaSchema.shape.offset.nullable(),
        timestamp: SourceKafkaSchema.shape.timestamp.nullable(),
      })
      .strict(),
  })
  .strict();

const EnrichedReplayAggregateSchema = z
  .object({
    replay_click_count: z.number().int().nonnegative().optional(),
    replay_engaged_time_ms: z.number().int().nonnegative().optional(),
    replay_chunk_count: z.number().int().nonnegative().nullable().optional(),
    has_session_replay: z.boolean().optional(),
    replay_metrics_updated_at: z.string().datetime({ offset: true }).optional(),
  })
  .strict();

const EnrichedEngagementSchema = z
  .object({
    is_engaged_session: z.boolean().optional(),
    page_view_state: PageViewStateSchema,
  })
  .strict();

const EnrichedIdentifiedEngagementSchema = z
  .object({
    is_engaged_session: z.boolean(),
    page_view_state: PageViewStateSchema,
  })
  .strict();

const EnrichedAnonymousClassificationSchema = z
  .object({
    is_bot: z.boolean(),
    is_known: z.literal(false),
  })
  .strict();

const EnrichedIdentifiedClassificationSchema = z
  .object({
    is_bot: z.boolean(),
  })
  .strict();

const EnrichedPageViewEventMetadataSchema = EnrichedSourceKafkaSchema.extend(EnrichedIdentitySchema.shape).extend(
  EnrichedIdentifiedEngagementSchema.shape,
);
const EnrichedPageViewBaseSchema = RawPageViewEventSchema.omit({ one_shot_token: true }).extend(
  EnrichedEventTsSchema.shape,
);
const EnrichedSixSenseSchema = z
  .object({
    six_sense_data: z.string().optional(),
  })
  .strict();

const OptionalSixSenseStringSchema = z.preprocess(
  (value) => {
    if (value === null) {
      return undefined;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? undefined : trimmed;
    }
    return value;
  },
  z
    .union([z.string(), z.number(), z.boolean()])
    .transform((value) => value.toString())
    .optional(),
);

const OptionalSixSenseStringArraySchema = z.preprocess(
  (value) => {
    if (value === null) {
      return undefined;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed === '' ? undefined : [trimmed];
    }
    return value;
  },
  z.array(z.string().trim().min(1)).optional().default([]),
);

const RawSixSenseIndustryV2Schema = z
  .object({
    subindustry: OptionalSixSenseStringSchema,
  })
  .passthrough();

const OptionalSixSenseIndustryV2ArraySchema = z.preprocess(
  (value) => (value === null ? undefined : value),
  z.array(RawSixSenseIndustryV2Schema).optional().default([]),
);

const RawSixSenseCompanyDataSchema = z
  .object({
    name: OptionalSixSenseStringSchema,
    company_name: OptionalSixSenseStringSchema,
    buying_stage: OptionalSixSenseStringSchema,
    buyingStage: OptionalSixSenseStringSchema,
    industry: OptionalSixSenseStringSchema,
    employee_range: OptionalSixSenseStringSchema,
    employeeRange: OptionalSixSenseStringSchema,
    revenue_range: OptionalSixSenseStringSchema,
    revenueRange: OptionalSixSenseStringSchema,
    confidence: OptionalSixSenseStringSchema,
    profile_fit: OptionalSixSenseStringSchema,
    profileFit: OptionalSixSenseStringSchema,
    domain: OptionalSixSenseStringSchema,
    product: OptionalSixSenseStringSchema,
    segments: OptionalSixSenseStringArraySchema,
    sub_industry: OptionalSixSenseStringArraySchema,
    subIndustry: OptionalSixSenseStringArraySchema,
    industry_v2: OptionalSixSenseIndustryV2ArraySchema,
  })
  .passthrough();

const RawSixSenseScoreSchema = z
  .object({
    buying_stage: OptionalSixSenseStringSchema,
    product: OptionalSixSenseStringSchema,
    product_display_name: OptionalSixSenseStringSchema,
    profile_fit: OptionalSixSenseStringSchema,
  })
  .passthrough();

const OptionalSixSenseScoreArraySchema = z.preprocess(
  (value) => (value === null ? undefined : value),
  z.array(RawSixSenseScoreSchema).optional().default([]),
);

const RawSixSenseSegmentsSchema = z
  .object({
    names: OptionalSixSenseStringArraySchema,
  })
  .passthrough();

const RawSixSenseResponseSegmentsSchema = z
  .union([OptionalSixSenseStringArraySchema, RawSixSenseSegmentsSchema])
  .optional()
  .default([]);

const RawSixSenseResponseSchema = RawSixSenseCompanyDataSchema.omit({ segments: true })
  .extend({
    company: RawSixSenseCompanyDataSchema,
    scores: OptionalSixSenseScoreArraySchema,
    segments: RawSixSenseResponseSegmentsSchema,
  })
  .passthrough();

function normalizeSixSenseCompanyData(company: z.infer<typeof RawSixSenseCompanyDataSchema>) {
  const industryV2SubIndustries = company.industry_v2.flatMap(({ subindustry }) =>
    subindustry === undefined ? [] : [subindustry],
  );

  return {
    name: company.name ?? company.company_name,
    buyingStage: company.buying_stage ?? company.buyingStage,
    industry: company.industry,
    employeeRange: company.employee_range ?? company.employeeRange,
    revenueRange: company.revenue_range ?? company.revenueRange,
    confidence: company.confidence,
    profileFit: company.profile_fit ?? company.profileFit,
    domain: company.domain,
    product: company.product,
    segments: company.segments,
    subIndustry:
      company.sub_industry.length > 0
        ? company.sub_industry
        : company.subIndustry.length > 0
          ? company.subIndustry
          : industryV2SubIndustries,
  };
}

function normalizeSixSenseResponse(response: z.infer<typeof RawSixSenseResponseSchema>) {
  const { company, scores, segments, ...root } = response;
  const rootSegments = Array.isArray(segments) ? segments : segments.names;
  const normalizedRoot = normalizeSixSenseCompanyData({ ...root, segments: rootSegments });
  const merged = mergeSixSenseCompanyData(normalizeSixSenseCompanyData(company), normalizedRoot);
  const firstScore = scores[0];

  return {
    ...merged,
    buyingStage: merged.buyingStage ?? firstScore?.buying_stage,
    confidence: normalizedRoot.confidence ?? merged.confidence,
    profileFit: merged.profileFit ?? firstScore?.profile_fit,
    product: merged.product ?? firstScore?.product_display_name ?? firstScore?.product,
    segments: normalizedRoot.segments.length > 0 ? normalizedRoot.segments : merged.segments,
  };
}

function mergeSixSenseCompanyData(
  primary: ReturnType<typeof normalizeSixSenseCompanyData>,
  fallback: ReturnType<typeof normalizeSixSenseCompanyData>,
) {
  return {
    name: primary.name ?? fallback.name,
    buyingStage: primary.buyingStage ?? fallback.buyingStage,
    industry: primary.industry ?? fallback.industry,
    employeeRange: primary.employeeRange ?? fallback.employeeRange,
    revenueRange: primary.revenueRange ?? fallback.revenueRange,
    confidence: primary.confidence ?? fallback.confidence,
    profileFit: primary.profileFit ?? fallback.profileFit,
    domain: primary.domain ?? fallback.domain,
    product: primary.product ?? fallback.product,
    segments: primary.segments.length > 0 ? primary.segments : fallback.segments,
    subIndustry: primary.subIndustry.length > 0 ? primary.subIndustry : fallback.subIndustry,
  };
}

export const SixSenseCompanyDataSchema = z.union([
  RawSixSenseResponseSchema.transform(normalizeSixSenseResponse),
  RawSixSenseCompanyDataSchema.transform(normalizeSixSenseCompanyData),
]);

export function parseSixSenseCompanyData(sixSenseData: string | null | undefined): SixSenseCompanyData | null {
  if (sixSenseData == null || sixSenseData.trim() === '') {
    return null;
  }

  try {
    return SixSenseCompanyDataSchema.parse(JSON.parse(sixSenseData));
  } catch {
    return null;
  }
}

const EnrichedAnonymousPageViewEventSchema = EnrichedPageViewBaseSchema.extend(EnrichedSourceKafkaSchema.shape)
  .extend(PageViewWafMetadataSchema.shape)
  .extend(EnrichedAnonymousClassificationSchema.shape)
  .extend(EnrichedSixSenseSchema.shape)
  .extend(EnrichedReplayAggregateSchema.shape)
  .extend(EnrichedEngagementSchema.shape);

const EnrichedIdentifiedPageViewEventSchema = EnrichedPageViewBaseSchema.extend(
  EnrichedPageViewEventMetadataSchema.shape,
)
  .extend(PageViewWafMetadataSchema.shape)
  .extend(EnrichedIdentifiedClassificationSchema.shape)
  .extend(EnrichedSixSenseSchema.shape)
  .extend(EnrichedReplayAggregateSchema.shape);

export const EnrichedPageViewEventSchema = z.union([
  EnrichedAnonymousPageViewEventSchema,
  EnrichedIdentifiedPageViewEventSchema,
]);

// The nested levels a producer may extend, rebuilt in strip mode for the tolerant reader below.
const AdditiveEnrichedPageViewNesting = {
  properties: RawPageViewPropertiesSchema.strip(),
  source_kafka: EnrichedSourceKafkaSchema.shape.source_kafka.strip(),
};

const ForwardCompatibleEnrichedPageViewEventSchema = z.union([
  EnrichedAnonymousPageViewEventSchema.extend(AdditiveEnrichedPageViewNesting).strip(),
  EnrichedIdentifiedPageViewEventSchema.extend(AdditiveEnrichedPageViewNesting).strip(),
]);

export type EnrichedPageViewReadResult =
  | { kind: 'event'; event: EnrichedPageViewEvent; unknownFields: string[] }
  | { kind: 'invalid'; error: z.ZodError };

function isFieldBag(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Derived by diffing the record against the parsed result, so it needs no list of extendable levels.
// Names only: the values are visitor data.
function unknownFieldPaths(record: unknown, declared: unknown, prefix = ''): string[] {
  if (!isFieldBag(record) || !isFieldBag(declared)) {
    return [];
  }

  return Object.entries(record).flatMap(([field, value]) => {
    const path = prefix === '' ? field : `${prefix}.${field}`;
    return field in declared ? unknownFieldPaths(value, declared[field], path) : [path];
  });
}

/**
 * The enricher ships on its own cadence, so an undeclared field must not be fatal — that would turn
 * a backward-compatible upstream deploy into an outage for every reader that has not caught up.
 * Such fields are reported in `unknownFields` and dropped, never passed through; drift in a field
 * the contract *does* declare is still invalid.
 */
export function readEnrichedPageViewEvent(record: unknown): EnrichedPageViewReadResult {
  const canonical = EnrichedPageViewEventSchema.safeParse(record);
  if (canonical.success) {
    return { kind: 'event', event: canonical.data, unknownFields: [] };
  }

  const tolerated = ForwardCompatibleEnrichedPageViewEventSchema.safeParse(record);
  if (!tolerated.success) {
    return { kind: 'invalid', error: tolerated.error };
  }

  return { kind: 'event', event: tolerated.data, unknownFields: unknownFieldPaths(record, tolerated.data) };
}

export const EnrichedIdentifyEventSchema = RawIdentifyEventSchema.extend(EnrichedEventTsSchema.shape).extend(
  EnrichedSourceKafkaSchema.shape,
);

export const EnrichedRrwebDataEventSchema = RawRrwebDataEventSchema.extend(EnrichedEventTsSchema.shape).extend(
  EnrichedSourceKafkaSchema.shape,
);

export const EnrichedAnalyticsEventSchema = z.union([EnrichedPageViewEventSchema, EnrichedIdentifyEventSchema]);

export const EnrichedAnalyticsStorageEventSchema = z.union([
  EnrichedAnalyticsEventSchema,
  EnrichedRrwebDataEventSchema,
]);

export const DlqEventSchema = z
  .object({
    error_message: z.string(),
    raw_value: z.string(),
    source_kafka: SourceKafkaSchema,
    key: z.string(),
  })
  .strict();

export const ONE_SHOT_TOKEN_URL_PARAM = 'mtl';
export const ONE_SHOT_TOKEN_API_PATH = '/v1/tokens';
export const PROVIDER_TOKEN_API_PATH = '/v1/provider-tokens';
export const PROVIDER_TOKEN_CONTEXT_API_PATH = '/v1/provider-tokens/context';
export const SIX_SENSE_INTEGRATION_API_PATH = '/v1/six-sense/integrations';
export const COMPANY_API_TOKEN_API_PATH = '/v1/company-api-tokens';
export const INTERNAL_TRAFFIC_RULE_API_PATH = '/v1/internal-traffic/rules';
export const MARK_INTERNAL_PAGE_VIEW_API_PATH = '/v1/internal-traffic/mark';
export const INTERNAL_API_SECRET_HEADER = 'x-internal-api-secret';

export const REPLAY_FLUSH_REASON_HEADER = 'x-mutiny-replay-flush-reason';
export const REPLAY_ATTEMPT_HEADER = 'x-mutiny-replay-attempt';
export const REPLAY_UNCOMPRESSED_BYTES_HEADER = 'x-mutiny-replay-uncompressed-bytes';
export const REPLAY_EVENT_COUNT_HEADER = 'x-mutiny-replay-event-count';

export const ReplayFlushReason = {
  InitialSnapshot: 'initial_snapshot',
  Interval: 'interval',
  Lifecycle: 'lifecycle',
  SizeLimit: 'size_limit',
} as const;

export type ReplayFlushReason = (typeof ReplayFlushReason)[keyof typeof ReplayFlushReason];

export const CompanyApiTokenRecordSchema = z
  .object({
    company_id: z.string().min(1),
    token_hash: z.string().min(1),
    updated_at: z.string().datetime(),
  })
  .strict();

export const UpsertCompanyApiTokenRequestSchema = z
  .object({
    company_id: z.string().min(1),
    api_token: z.string().min(1),
  })
  .strict();

const InternalTrafficIpAddressSchema = z
  .string()
  .trim()
  .pipe(z.union([z.ipv4(), z.ipv6()]));

export const CreateInternalIpRuleRequestSchema = z
  .object({
    company_id: z.string().min(1),
    ip_address: InternalTrafficIpAddressSchema,
  })
  .strict();

export const MarkInternalPageViewRequestSchema = z
  .object({
    company_id: z.string().min(1),
    asset_id: z.string().min(1),
    event_id: z.string().min(1),
  })
  .strict();

export const OneShotTokenState = {
  Active: 'active',
  Pending: 'pending',
  Used: 'used',
  Failed: 'failed',
} as const;

export const SalesloftProviderContextSchema = z
  .object({
    version: z.literal(1),
    type: z.literal('salesloft'),
    sender_email: z.string().email(),
    email_id: z.string().min(1),
  })
  .strict();

export const OutreachProviderContextSchema = z
  .object({
    version: z.literal(1),
    type: z.literal('outreach'),
    sender_email: z.string().email(),
    mailing_id: z.string().min(1),
    prospect_id: z.string().min(1).optional(),
    sequence_id: z.string().min(1).optional(),
    sequence_state_id: z.string().min(1).optional(),
    sequence_step_id: z.string().min(1).optional(),
    template_id: z.string().min(1).optional(),
  })
  .strict();

export const OneShotProviderContextSchema = z.discriminatedUnion('type', [
  SalesloftProviderContextSchema,
  OutreachProviderContextSchema,
]);

const BaseOneShotTokenRecordSchema = z
  .object({
    company_id: z.string().min(1),
    token: z.string().min(1),
    asset_id: z.string().min(1).optional(),
    email: z.string().email(),
    traits: z.record(z.string(), z.string()),
    target_urls: z.array(z.string().url()).min(1),
    created_at: z.string().datetime(),
    provider_context: OneShotProviderContextSchema.optional(),
  })
  .strict();

export const ActiveOneShotTokenRecordSchema = BaseOneShotTokenRecordSchema.extend({
  state: z.literal(OneShotTokenState.Active),
});

export const PendingOneShotTokenRecordSchema = BaseOneShotTokenRecordSchema.extend({
  state: z.literal(OneShotTokenState.Pending),
  claim_id: z.string().uuid(),
  claimed_at: z.string().datetime(),
});

export const UsedOneShotTokenRecordSchema = BaseOneShotTokenRecordSchema.extend({
  state: z.literal(OneShotTokenState.Used),
  used_at: z.string().datetime(),
});

export const FailedOneShotTokenRecordSchema = BaseOneShotTokenRecordSchema.extend({
  state: z.literal(OneShotTokenState.Failed),
  failed_at: z.string().datetime(),
});

export const OneShotTokenRecordSchema = z.discriminatedUnion('state', [
  ActiveOneShotTokenRecordSchema,
  PendingOneShotTokenRecordSchema,
  UsedOneShotTokenRecordSchema,
  FailedOneShotTokenRecordSchema,
]);

export const CreateOneShotTokensRequestSchema = z
  .object({
    company_id: z.string().min(1),
    asset_id: z.string().min(1).optional(),
    email: z.string().email(),
    traits: z.record(z.string(), z.string()).optional(),
    urls: z.array(z.string().url()).min(1).max(100),
  })
  .strict();

export const CreateOneShotTokensResponseSchema = z
  .object({
    mapped_urls: z.record(z.string().url(), z.string().url()),
  })
  .strict();

const ProviderTokenRequestBaseSchema = z
  .object({
    company_id: z.string().min(1),
    asset_id: z.string().min(1).optional(),
    recipient_email: z.string().email(),
    urls: z.array(z.string().url()).min(1).max(100),
  })
  .strict();

const SalesloftProviderTokenRequestSchema = ProviderTokenRequestBaseSchema.extend({
  provider: z
    .object({
      type: z.literal('salesloft'),
      sender_email: z.string().email(),
      email_id: z.string().min(1),
    })
    .strict(),
});

const OutreachProviderTokenRequestSchema = ProviderTokenRequestBaseSchema.extend({
  provider: z
    .object({
      type: z.literal('outreach'),
      sender_email: z.string().email(),
      mailing_id: z.string().min(1),
      prospect_id: z.string().min(1).optional(),
    })
    .strict(),
});

export const CreateProviderTokenRequestSchema = z.union([
  SalesloftProviderTokenRequestSchema,
  OutreachProviderTokenRequestSchema,
]);

export const CreateProviderTokenResponseSchema = z
  .object({
    provider_token: z.string().min(1),
    mapped_urls: z.record(z.string().url(), z.string().url()),
  })
  .strict();

export const OutreachProviderContextEnrichmentSchema = z
  .object({
    type: z.literal('outreach'),
    sequence_id: z.string().min(1).optional(),
    sequence_state_id: z.string().min(1).optional(),
    sequence_step_id: z.string().min(1).optional(),
    template_id: z.string().min(1).optional(),
  })
  .strict()
  .refine(
    (context) =>
      context.sequence_id !== undefined ||
      context.sequence_state_id !== undefined ||
      context.sequence_step_id !== undefined ||
      context.template_id !== undefined,
    { message: 'At least one Outreach enrichment field is required.' },
  );

export const UpdateOutreachProviderContextRequestSchema = z
  .object({
    company_id: z.string().min(1),
    provider_token: z.string().min(1),
    provider_context: OutreachProviderContextEnrichmentSchema,
  })
  .strict();

export const SixSenseIntegrationTypeSchema = z.enum(['user-defined', 'bundled']);

export const SixSenseIntegrationRecordSchema = z
  .object({
    company_id: z.string().min(1),
    company_slug: z.string().min(1),
    token: z.string().min(1),
    integration_type: SixSenseIntegrationTypeSchema,
    active: z.literal(true),
    updated_at: z.string().datetime(),
  })
  .strict();

const FlatUpsertSixSenseIntegrationRequestSchema = z
  .object({
    company_id: z.string().min(1),
    company_slug: z.string().min(1),
    token: z.string().min(1),
    integration_type: SixSenseIntegrationTypeSchema,
  })
  .strict();

const WrappedUpsertSixSenseIntegrationRequestSchema = z
  .object({
    company_id: z.string().min(1),
    company_slug: z.string().min(1),
    credentials: z
      .object({
        token: z.string().min(1),
      })
      .strict(),
    mutiny_plan: z.boolean(),
  })
  .strict()
  .transform((request) => ({
    company_id: request.company_id,
    company_slug: request.company_slug,
    token: request.credentials.token,
    integration_type: request.mutiny_plan
      ? SixSenseIntegrationTypeSchema.enum.bundled
      : SixSenseIntegrationTypeSchema.enum['user-defined'],
  }));

export const UpsertSixSenseIntegrationRequestSchema = z.union([
  FlatUpsertSixSenseIntegrationRequestSchema,
  WrappedUpsertSixSenseIntegrationRequestSchema,
]);

export type ServerEventFields = z.infer<typeof ServerEventFieldsSchema>;
export type DeviceType = z.infer<typeof DeviceTypeSchema>;
export type RawPageViewEvent = z.infer<typeof RawPageViewEventSchema>;
export type RawAnalyticsEvent = z.infer<typeof RawAnalyticsEventSchema>;
export type RawAnalyticsStorageEvent = z.infer<typeof RawAnalyticsStorageEventSchema>;
export type RawIdentifyEvent = z.infer<typeof RawIdentifyEventSchema>;
export type RawRrwebClickEvent = z.infer<typeof RawRrwebClickEventSchema>;
export type RawRrwebTimeEvent = z.infer<typeof RawRrwebTimeEventSchema>;
export type RawRrwebDataEvent = z.infer<typeof RawRrwebDataEventSchema>;
export type RecordingEvent = z.infer<typeof RecordingEventSchema>;
export type SourceKafka = z.infer<typeof SourceKafkaSchema>;
export type PageViewWafMetadata = z.infer<typeof PageViewWafMetadataSchema>;
export type EnrichedIdentity = z.infer<typeof EnrichedIdentitySchema>;
export type VisitorIdentity = z.infer<typeof VisitorIdentitySchema>;
export type PageViewState = z.infer<typeof PageViewStateSchema>;
export type EnrichedPageViewEvent = z.infer<typeof EnrichedPageViewEventSchema>;
export type EnrichedAnalyticsEvent = z.infer<typeof EnrichedAnalyticsEventSchema>;
export type EnrichedAnalyticsStorageEvent = z.infer<typeof EnrichedAnalyticsStorageEventSchema>;
export type DlqEvent = z.infer<typeof DlqEventSchema>;
export type SixSenseCompanyData = z.infer<typeof SixSenseCompanyDataSchema>;
export type OneShotTokenRecord = z.infer<typeof OneShotTokenRecordSchema>;
export type CompanyApiTokenRecord = z.infer<typeof CompanyApiTokenRecordSchema>;
export type CreateOneShotTokensRequest = z.infer<typeof CreateOneShotTokensRequestSchema>;
export type CreateOneShotTokensResponse = z.infer<typeof CreateOneShotTokensResponseSchema>;
export type CreateProviderTokenRequest = z.infer<typeof CreateProviderTokenRequestSchema>;
export type CreateProviderTokenResponse = z.infer<typeof CreateProviderTokenResponseSchema>;
export type OneShotProvider = z.infer<typeof OneShotProviderSchema>;
export type OneShotProviderContext = z.infer<typeof OneShotProviderContextSchema>;
export type OutreachProviderContextEnrichment = z.infer<typeof OutreachProviderContextEnrichmentSchema>;
export type UpdateOutreachProviderContextRequest = z.infer<typeof UpdateOutreachProviderContextRequestSchema>;
export type UpsertCompanyApiTokenRequest = z.infer<typeof UpsertCompanyApiTokenRequestSchema>;
export type CreateInternalIpRuleRequest = z.infer<typeof CreateInternalIpRuleRequestSchema>;
export type MarkInternalPageViewRequest = z.infer<typeof MarkInternalPageViewRequestSchema>;
export type SixSenseIntegrationRecord = z.infer<typeof SixSenseIntegrationRecordSchema>;
export type UpsertSixSenseIntegrationRequest = z.infer<typeof UpsertSixSenseIntegrationRequestSchema>;
